import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";

const FAQS = [
  {
    q: "How does the vetting work?",
    a: "Every practitioner is personally reviewed by the O.R.E.C team before going live. You create your profile — your story, services, and pricing — and it stays private until it's approved. We look for genuine skill, integrity, and heart. When you're approved, you'll receive a welcome email and your profile becomes visible to the whole community.",
  },
  {
    q: "What does it cost to join?",
    a: "Your Leader listing is free for your entire first year — the moment the Team approves you, your profile is on the practitioners list at no charge. After your first year it's $15/month to stay listed, month to month, on or off anytime you choose. On bookings, O.R.E.C keeps a 10% community fee and you keep 90%. Want extra visibility? The Featured page spotlight is the only paid extra — $25/month, matched to seekers by location and interests, and only if you choose it.",
  },
  {
    q: "How do I get paid?",
    a: "Seekers pay securely by card when they book. Your 90% share is tracked automatically in your Studio dashboard under Earnings. Connect your bank once through Stripe (about five minutes, from your Studio) and your share is transferred automatically on the 1st and 15th of every month for sessions that have taken place. Prefer not to connect? O.R.E.C can still pay you by e-transfer from your ledger. Every booking, amount, and payout is visible in one place at any time.",
  },
  {
    q: "How do bookings work?",
    a: "You set your own services, prices, and weekly availability in your Studio. Seekers pick an open slot and pay upfront — you get an email confirmation, and the booking appears in your dashboard. You stay in full control of your calendar.",
  },
  {
    q: "What about reviews?",
    a: "Seekers can leave a review after a completed session. O.R.E.C uses a fair three-tier system — and any 1-star review is held privately first, giving you and the seeker a chance to resolve things before anything goes public. Your reputation is treated with care here.",
  },
  {
    q: "How do I get started?",
    a: "Create an account as a practitioner, fill in your profile (photo, story, services, prices, availability), and submit. The Team reviews it personally — most profiles hear back quickly. Once approved, you're live and bookable.",
  },
];

export default function PractitionerFAQ() {
  const navigate = useNavigate();
  return (
    <div className="silk-hero min-h-[70vh] px-5 sm:px-8 py-16">
      <SEO title="How Joining Works — O.R.E.C Practitioners" description="Vetting, fees, payouts and bookings — everything practitioners need to know before joining Our Ripple Effect Community." />
      <div className="max-w-2xl mx-auto" data-testid="practitioner-faq-page">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">For Practitioners</p>
        <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-3">How joining works</h1>
        <p className="text-sm text-muted-foreground mb-10 leading-relaxed">
          O.R.E.C is a hand-vetted community — good people doing good work. Here's everything you need to know before listing your practice.
        </p>
        <Accordion type="single" collapsible className="silk-card rounded-lg px-6" data-testid="practitioner-faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-base md:text-lg text-foreground hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground mb-5">Ready to start your Ripple?</p>
          <Button data-testid="faq-join-btn" className="rounded-full px-8" onClick={() => navigate("/register")}>
            List your practice <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
